import { Input, InputIcon, NavList, Text } from '@payaca/ambrosia';
import { FC, useState, type JSX } from 'react';
import { useLocation } from 'react-router';
import { Link, NavLink } from 'react-router-dom';

import { useSettingsNav } from './useSettingsNav';

type Props = {
  /** Lets the shell dismiss the nav once a section is chosen on small screens. */
  onNavigate?: () => void;
};

const routeMatches = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

/**
 * Categories collapse by default; the active one stays open, searching expands
 * any category with matches, and a chevron toggles the rest. The search box
 * filters by section name or the settings a section contains.
 */
const SettingsNav: FC<Props> = (props: Props): JSX.Element => {
  const { onNavigate } = props;
  const categories = useSettingsNav();
  const { pathname } = useLocation();
  const [filter, setFilter] = useState('');
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>(
    {}
  );

  const query = filter.trim().toLowerCase();
  const isSubActive = (route: string) => routeMatches(pathname, route);
  const activeCategoryLabel = categories.find((category) =>
    category.subSections.some((sub) => isSubActive(sub.route))
  )?.label;

  // A word boundary so "vat" finds "VAT" but not "deactivate". A query opening
  // with a non-word character has no boundary, so falls back to a substring match.
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matcher = query
    ? new RegExp(/^\w/.test(query) ? `\\b${escapedQuery}` : escapedQuery, 'i')
    : null;
  const textMatches = (text: string) => !matcher || matcher.test(text);

  const matchingCategories = categories
    .map((category) => {
      const categoryMatches = textMatches(category.label);
      const subSections = category.subSections.flatMap((sub) => {
        if (!query || categoryMatches || textMatches(sub.label)) {
          return [{ sub, hint: undefined as string | undefined }];
        }
        // Matched by one of the settings the section contains — surface it.
        const hint = sub.keywords.find((keyword) => textMatches(keyword));
        return hint ? [{ sub, hint }] : [];
      });
      return { category, subSections };
    })
    .filter(
      ({ category, subSections }) =>
        !query || subSections.length > 0 || textMatches(category.label)
    );

  return (
    <nav
      aria-label="Company settings"
      // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
      className="w-full md:sticky md:top-0 md:max-h-[calc(100vh-4rem)] md:overflow-y-auto"
    >
      <div className="p-3">
        <Input
          sizeVariant="sm"
          placeholder="Search settings"
          aria-label="Search settings"
          value={filter}
          onChange={setFilter}
          leadingElement={<InputIcon name="search-lg.3" />}
        />
      </div>
      <div aria-live="polite" className="sr-only">
        {query
          ? `${matchingCategories.length} settings categories match ${query}`
          : ''}
      </div>
      {query && matchingCategories.length === 0 ? (
        <div className="px-3 pb-3">
          <Text level="supporting">
            No settings match &ldquo;{filter.trim()}&rdquo;.
          </Text>
        </div>
      ) : (
        <NavList className="px-3 pb-3">
          {matchingCategories.map(({ category, subSections }) => {
            return (
              <NavList.Group
                key={category.label}
                open={
                  query
                    ? true
                    : (openOverrides[category.label] ??
                      category.label === activeCategoryLabel)
                }
                onOpenChange={(open) =>
                  setOpenOverrides((prev) => ({
                    ...prev,
                    [category.label]: open,
                  }))
                }
              >
                <NavList.GroupHeader
                  as={Link}
                  to={category.route}
                  onClick={onNavigate}
                  triggerLabel={
                    // Categories are force-expanded while searching, so a toggle
                    // could not do what it announces.
                    query ? undefined : `Toggle ${category.label} section`
                  }
                >
                  {category.label}
                </NavList.GroupHeader>
                <NavList.GroupPanel>
                  {subSections.map(({ sub, hint }) => (
                    <NavList.Item
                      key={sub.label}
                      as={NavLink}
                      to={sub.route}
                      onClick={onNavigate}
                    >
                      {hint ? (
                        // So the row explains why the search surfaced it.
                        <span className="flex min-w-0 flex-col">
                          <Text as="span">{sub.label}</Text>
                          <Text as="span" level="supporting">
                            {hint}
                          </Text>
                        </span>
                      ) : (
                        sub.label
                      )}
                    </NavList.Item>
                  ))}
                </NavList.GroupPanel>
              </NavList.Group>
            );
          })}
        </NavList>
      )}
    </nav>
  );
};

export default SettingsNav;
